import { resolveChecklistMediaUri } from '@/utils/media/resolveChecklistMediaUri';
import { Asset } from 'expo-asset';
import { observationKey } from './checklist/checklistObservations';
import { PDFDocument, PDFString, rgb, StandardFonts, appendBezierCurve, clip, closePath, endPath, lineTo, moveTo, popGraphicsState, pushGraphicsState, type PDFFont, type PDFImage, type RGB } from 'pdf-lib';
import { databaseService } from './databaseService';
import * as FileSystem from 'expo-file-system/legacy';
import APP_CONSTANTS from '@/config/constants';
import { Checklist, ChecklistField } from '@/hooks/useChecklistEditor';
import { PDFDocBuilderLogger } from '@/utils/logger';
import { Buffer } from 'buffer';
import { checklistHistoricalReadService } from '@/services/checklist/ChecklistHistoricalReadRuntime';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { base64ToArrayBuffer } from '@/utils/file/base64Helpers';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';

/**
 * Logger padrão do builder compartilhado de PDF (web e nativo).
 */
const PdfDocLogger = PDFDocBuilderLogger;

/** Parâmetros para construção do documento PDF. */
export interface GenerateChecklistPdfParams {
  checklist: Checklist;
  template: ChecklistField[];
  values: Record<string, string | null | undefined> | null;
  /** Entrada pública explícita: o renderer não consulta histórico, banco nem mídia administrativa. */
  publicSnapshot?: {
    company: { id: string; nome?: string | null; cnpj?: string | null; telefone?: string | null; email?: string | null;
      endereco?: string | null; cidade?: string | null; uf?: string | null; cep?: string | null; logo_url?: string | null };
    fieldIds: readonly string[];
    mediaMetadata: Record<string, { created_at?: string | null; updated_at?: string | null }>;
  };
}

// ------------------ Constantes de Layout ------------------ //
export const LAYOUT = {
  PAGE_MARGIN: 40,
  LINE_HEIGHT: 16,
  LABEL_FONT_SIZE: 12,
  VALUE_FONT_SIZE: 12,
  SECTION_TITLE_FONT_SIZE: 14,
  HEADER_FONT_SIZE: 18,
  LOGO_MAX_WIDTH: 120,
  LOGO_MAX_HEIGHT: 80,
  HEADER_LINE_SPACING: 2,
  IMAGE_MAX_WIDTH: 400,
  IMAGE_MAX_HEIGHT: 300,
  GROUP_BG_RGB: { r: 0.93, g: 0.93, b: 0.93 } as const,
  GROUP_HEADER_PADDING_Y: 4,
  GROUP_AFTER_SPACING: 8,
  INLINE_TEXT_GAP: 4,
  MIN_INLINE_VALUE_WIDTH: 96,
  TEXT_CONTINUATION_INDENT: 12,
  BULLET: '• ',
} as const;

/** Tipos de campo considerados imagens. */
export const IMAGE_FIELD_TYPES = ['foto', 'photo', 'assinatura', 'signature', 'marcação', 'marcacao', 'mark'];
const MEDIA_OBSERVATION_FIELD_TYPES = new Set(['foto', 'photo', 'marcação', 'marcacao', 'mark']);

/** Classificação da falha ao carregar uma imagem para o PDF. */
export type PdfImageLoadFailureKind = 'network' | 'unavailable' | 'local' | 'invalid' | 'unknown';

type PreparedPdfMediaImage = {
  image: PDFImage;
  imageWidth: number;
  imageHeight: number;
  scale: number;
  blockHeight: number;
  labelLines: string[];
};

type PdfMediaImagePreparation =
  | { status: 'prepared'; value: PreparedPdfMediaImage }
  | { status: 'failed'; error: unknown; stage: 'read' | 'embed' };

/** Mensagens curtas para o usuário tentar de novo conforme a causa. */
export const PDF_IMAGE_LOAD_FAILURE_MESSAGES: Record<PdfImageLoadFailureKind, string> = {
  network:
    'Sua rede está instável e a imagem não foi baixada. Tente gerar o PDF novamente quando a conexão estiver melhor.',
  unavailable:
    'A imagem deste campo não está disponível agora. Tente gerar o PDF novamente em instantes.',
  local:
    'Não foi possível ler a imagem salva neste aparelho. Tente gerar o PDF novamente.',
  invalid:
    'A imagem deste campo está inválida ou corrompida. Tente capturar novamente e gerar o PDF de novo.',
  unknown:
    'Não foi possível incluir a imagem neste PDF. Tente gerar novamente.',
};

const NETWORK_FAILURE_HINTS = [
  'failed to fetch',
  'network request failed',
  'networkerror',
  'falha ao baixar recurso',
  'timeout',
  'timed out',
  'econnreset',
  'enotfound',
  'eai_again',
  'socket',
  'certificate',
  'abort',
];

const LOCAL_FAILURE_HINTS = [
  'no such file',
  'enoent',
  'could not be read',
  'falha ao ler arquivo local',
  'unable to read',
  'file does not exist',
];

const INVALID_IMAGE_HINTS = [
  'invalid jpeg',
  'invalid png',
  'failed to parse',
  'unexpected eof',
  'not a jpeg',
  'not a png',
  'png signature',
  'soi not found',
  'embedjpg',
  'embedpng',
];

const collectErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    const causeText = error.cause instanceof Error
      ? `${error.cause.name} ${error.cause.message}`
      : error.cause
        ? String(error.cause)
        : '';
    return `${error.name} ${error.message} ${causeText}`.toLowerCase();
  }
  return String(error ?? '').toLowerCase();
};

const isRemoteImageSource = (source: string): boolean => (
  source.startsWith('http://') || source.startsWith('https://')
);

const isLocalImageSource = (source: string): boolean => (
  source.startsWith('file://') ||
  source.startsWith('blob:') ||
  source.startsWith('content://') ||
  source.startsWith('ph://') ||
  source.startsWith('checklist-')
);

const extractHttpStatus = (error: unknown): number | null => {
  const match = collectErrorText(error).match(/falha ao baixar recurso \((\d{3})\)/);
  if (!match) return null;
  const status = Number(match[1]);
  return Number.isFinite(status) ? status : null;
};

/** Decide a causa da falha para escolher a mensagem no PDF. */
export function classifyPdfImageLoadFailure(
  error: unknown,
  source: string,
): PdfImageLoadFailureKind {
  const text = collectErrorText(error);
  const value = String(source ?? '').trim();
  const httpStatus = extractHttpStatus(error);

  if (INVALID_IMAGE_HINTS.some(hint => text.includes(hint))) {
    return 'invalid';
  }
  if (value.startsWith('data:')) {
    return 'invalid';
  }
  if (httpStatus != null && httpStatus >= 400 && httpStatus < 500) {
    return 'unavailable';
  }
  if (isLocalImageSource(value)) {
    return 'local';
  }
  if (isRemoteImageSource(value) || httpStatus != null || NETWORK_FAILURE_HINTS.some(hint => text.includes(hint))) {
    return 'network';
  }
  if (LOCAL_FAILURE_HINTS.some(hint => text.includes(hint))) {
    return 'local';
  }
  return 'unknown';
}

export function getPdfImageLoadFailureMessage(kind: PdfImageLoadFailureKind): string {
  return PDF_IMAGE_LOAD_FAILURE_MESSAGES[kind] ?? PDF_IMAGE_LOAD_FAILURE_MESSAGES.unknown;
}

const describeImageSourceForLog = (source: string): Record<string, string | null> => {
  const value = String(source ?? '');
  if (isRemoteImageSource(value)) {
    try {
      const parsed = new URL(value);
      return { sourceKind: 'remote', sourceHost: parsed.host };
    } catch {
      return { sourceKind: 'remote', sourceHost: null };
    }
  }
  if (isLocalImageSource(value)) {
    return { sourceKind: 'local', sourceHost: null };
  }
  if (value.startsWith('data:')) {
    return { sourceKind: 'data', sourceHost: null };
  }
  return { sourceKind: 'other', sourceHost: null };
};

const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '');
  const parsed = parseInt(normalized, 16);
  return {
    r: ((parsed >> 16) & 255) / 255,
    g: ((parsed >> 8) & 255) / 255,
    b: (parsed & 255) / 255,
  };
};

const LABEL_COLOR = rgb(0.4, 0.47, 0.55);
const VALUE_COLOR = rgb(0.08, 0.19, 0.33);
const LINK_COLOR = rgb(0, 0.4, 0.8);
const ORIGINAL_HEADER_COMPANY_ID = '2b0de2b0-a0f3-4cd3-ad7d-6f28f26dd5e3';
const APP_SITE = 'https://www.horizonteinteligencia.com.br/portfolio/guincho';
const APP_WHATSAPP = 'https://wa.me/5531983364526';
// Ícone WhatsApp do Material Design Icons (Apache-2.0), também usado no app.
const WHATSAPP_ICON_PATH = 'M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2M12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 15 3.8 13.47 3.8 11.91C3.81 7.37 7.5 3.67 12.05 3.67M8.53 7.33C8.37 7.33 8.1 7.39 7.87 7.64C7.65 7.89 7 8.5 7 9.71C7 10.93 7.89 12.1 8 12.27C8.14 12.44 9.76 14.94 12.25 16C12.84 16.27 13.3 16.42 13.66 16.53C14.25 16.72 14.79 16.69 15.22 16.63C15.7 16.56 16.68 16.03 16.89 15.45C17.1 14.87 17.1 14.38 17.04 14.27C16.97 14.17 16.81 14.11 16.56 14C16.31 13.86 15.09 13.26 14.87 13.18C14.64 13.1 14.5 13.06 14.31 13.3C14.15 13.55 13.67 14.11 13.53 14.27C13.38 14.44 13.24 14.46 13 14.34C12.74 14.21 11.94 13.95 11 13.11C10.26 12.45 9.77 11.64 9.62 11.39C9.5 11.15 9.61 11 9.73 10.89C9.84 10.78 10 10.6 10.1 10.45C10.23 10.31 10.27 10.2 10.35 10.04C10.43 9.87 10.39 9.73 10.33 9.61C10.27 9.5 9.77 8.26 9.56 7.77C9.36 7.29 9.16 7.35 9 7.34C8.86 7.34 8.7 7.33 8.53 7.33Z';

const STATUS_CHIP = {
  done: {
    label: 'Checklist concluído',
    color: hexToRgb('#34D399'),
  },
  in_progress: {
    label: 'Checklist em andamento',
    color: hexToRgb('#FBBF24'),
  },
} as const;

// ------------------ Helpers ------------------ //
const CHECKLIST_LOCAL_MEDIA_TABLES: Record<string, string> = {
  'checklist-photos': APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME,
  'checklist-signatures': APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME,
  'checklist-marcacoes': APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME,
};

interface ChecklistMediaScope {
  checklistId: string;
  companyId: string;
  fieldId: string;
  originalFieldId?: string | null;
}

async function fetchArrayBuffer(url: string, mediaScope?: ChecklistMediaScope, publicOnly = false): Promise<ArrayBuffer> {
  if (publicOnly) {
    if (mediaScope && !/^https?:\/\//i.test(url) && !/^data:image\/(png|jpeg|webp);base64,/i.test(url)) {
      throw new Error('Mídia indisponível no comprovante compartilhado.');
    }
  } else url = await resolveChecklistMediaUri(url, mediaScope);
  if (url.startsWith('data:')) {
    const base64 = url.split(',')[1];
    const binary = typeof atob === 'function' ? atob(base64) : Buffer.from(base64, 'base64').toString('binary');
    const len = binary.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) arr[i] = binary.charCodeAt(i);
    return arr.buffer;
  }
  const localPrefix = url.split('/', 1)[0];
  if (Object.prototype.hasOwnProperty.call(CHECKLIST_LOCAL_MEDIA_TABLES, localPrefix)) {
    if (publicOnly) throw new Error('Mídia local não permitida no comprovante.');
    const [prefix, companyId, fileName, ...extraSegments] = url.split('/');
    const fieldIds = [mediaScope?.fieldId, mediaScope?.originalFieldId].filter((fieldId): fieldId is string => Boolean(fieldId));
    const belongsToChecklist = mediaScope
      && companyId === mediaScope.companyId
      && extraSegments.length === 0
      && fieldIds.some(fieldId => fileName?.startsWith(`${mediaScope.checklistId}-${fieldId}-`));
    if (!belongsToChecklist) {
      throw new Error('A imagem local não pertence a este checklist.');
    }
    const localImage = await databaseService.readItem<{ id: string; base64: string }>(
      CHECKLIST_LOCAL_MEDIA_TABLES[prefix],
      url
    );
    if (!localImage?.base64 || localImage.id !== url) {
      throw new Error('A imagem local não está disponível no aparelho.');
    }
    return base64ToArrayBuffer(localImage.base64);
  }
  if (url.startsWith('file://')) {
    try {
      const encoding = FileSystem.EncodingType?.Base64 ?? 'base64';
      const base64 = await FileSystem.readAsStringAsync(url, { encoding: encoding as 'base64' });
      const binary = typeof atob === 'function' ? atob(base64) : Buffer.from(base64, 'base64').toString('binary');
      const len = binary.length;
      const arr = new Uint8Array(len);
      for (let i = 0; i < len; i++) arr[i] = binary.charCodeAt(i);
      return arr.buffer;
    } catch {
      // Evita enviar o caminho local completo ao Sentry.
      throw new Error('Falha ao ler arquivo local');
    }
  }
  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Falha ao baixar recurso (${res.status})`);
    }
    return await res.arrayBuffer();
  } catch (e) {
    if (e instanceof Error && e.message.startsWith('Falha ao baixar recurso')) {
      throw e;
    }
    throw new Error('Falha ao baixar recurso', { cause: e });
  }
}

async function embedPdfImage(pdfDoc: PDFDocument, bytes: ArrayBuffer, source: string): Promise<PDFImage> {
  const header = new Uint8Array(bytes, 0, Math.min(12, bytes.byteLength));
  const isWebP = header.length === 12 && String.fromCharCode(...header.slice(0, 4)) === 'RIFF'
    && String.fromCharCode(...header.slice(8, 12)) === 'WEBP';
  if (isWebP) {
    // A conversão usa somente os bytes já autorizados, sem resolver dados administrativos.
    const converted = await manipulateAsync(`data:image/webp;base64,${Buffer.from(bytes).toString('base64')}`, [], {
      format: SaveFormat.PNG, base64: true,
    });
    try {
      if (!converted.base64) throw new Error('Imagem inválida após conversão de WebP.');
      return await pdfDoc.embedPng(base64ToArrayBuffer(converted.base64));
    } finally {
      if (converted.uri.startsWith('file://')) await FileSystem.deleteAsync(converted.uri, { idempotent: true }).catch(() => {});
    }
  }
  const isPng = header[0] === 137 && header[1] === 80 && header[2] === 78 && header[3] === 71;
  return isPng || /image\/png|\.png(?:$|\?|#)/i.test(source)
    ? pdfDoc.embedPng(bytes) : pdfDoc.embedJpg(bytes);
}

export function formatDate(ts: any): string {
  if (!ts) return '';
  let d: Date;
  if (typeof ts === 'number') d = new Date(ts);
  else if (typeof ts === 'string') d = /^\d+$/.test(ts) ? new Date(Number(ts)) : new Date(ts);
  else return '';
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear().toString().slice(-2)} - ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatDateFull(ts: any): string {
  if (!ts) return '';
  let d: Date;
  if (typeof ts === 'number') d = new Date(ts);
  else if (typeof ts === 'string') d = /^\d+$/.test(ts) ? new Date(Number(ts)) : new Date(ts);
  else return '';
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} - ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const photoDateFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

export function formatPhotoUpdatedAt(ts: string | number | null | undefined): string {
  if (ts == null || ts === '') return '';
  const date = new Date(typeof ts === 'string' && /^\d+$/.test(ts) ? Number(ts) : ts);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(photoDateFormatter.formatToParts(date).map(part => [part.type, part.value]));
  return `${parts.day}/${parts.month}/${parts.year} - ${parts.hour}:${parts.minute}:${parts.second}`;
}

function splitWordToWidth(word: string, font: PDFFont, fontSize: number, maxWidth: number): [string, string] {
  const characters = Array.from(word);
  let low = 1;
  let high = characters.length;
  let fittingLength = 0;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const candidate = characters.slice(0, middle).join('');
    if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
      fittingLength = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  const safeLength = Math.max(fittingLength, 1);
  return [characters.slice(0, safeLength).join(''), characters.slice(safeLength).join('')];
}

/** Normaliza o texto e remove somente caracteres que a fonte do PDF não consegue codificar. */
export function sanitizeTextForPdf(text: string, font: PDFFont): string {
  const normalized = String(text).normalize('NFKC');
  let sanitized = '';

  for (const character of Array.from(normalized)) {
    if (character === '\n' || character === '\r' || character === '\t') {
      sanitized += character;
      continue;
    }
    try {
      font.encodeText(character);
      sanitized += character;
    } catch {
      // Caracteres invisíveis, emojis e outros glifos ausentes não devem interromper o PDF.
    }
  }

  return sanitized;
}

/** Quebra texto por largura, preservando parágrafos e dividindo palavras longas quando necessário. */
export function wrapTextToWidth(
  text: string,
  font: PDFFont,
  fontSize: number,
  maxWidth: number,
  firstLineMaxWidth = maxWidth,
): string[] {
  const lines: string[] = [];
  const paragraphs = sanitizeTextForPdf(text, font).replace(/\r\n?/g, '\n').split('\n');

  paragraphs.forEach((paragraph) => {
    const words = paragraph.trim().split(/\s+/u).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      return;
    }

    let currentLine = '';
    for (const originalWord of words) {
      let word = originalWord;
      while (word) {
        const lineMaxWidth = lines.length === 0 ? firstLineMaxWidth : maxWidth;
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (font.widthOfTextAtSize(candidate, fontSize) <= lineMaxWidth) {
          currentLine = candidate;
          word = '';
          continue;
        }

        if (currentLine) {
          lines.push(currentLine);
          currentLine = '';
          continue;
        }

        const [fittingPart, remainder] = splitWordToWidth(word, font, fontSize, lineMaxWidth);
        if (remainder) {
          lines.push(fittingPart);
        } else {
          currentLine = fittingPart;
        }
        word = remainder;
      }
    }

    if (currentLine) lines.push(currentLine);
  });

  return lines.length > 0 ? lines : [''];
}

/**
 * Constrói o documento PDF completo, mas NÃO o salva.
 * Retorna o objeto PDFDocument para que cada plataforma finalize como preferir.
 */
export async function buildChecklistPdfDoc({ checklist, template, values, publicSnapshot }: GenerateChecklistPdfParams): Promise<PDFDocument> {
  PdfDocLogger.Main.info('[PDF] buildChecklistPdfDoc iniciado', { checklistId: checklist?.id });
  if (!checklist) throw new Error('Checklist inválido');
  if (!values) throw new Error('Valores do checklist não carregados');

  if (publicSnapshot) {
    if (publicSnapshot.company.id !== checklist.empresa_id) throw new Error('Comprovante inválido.');
    const allowed = new Set(publicSnapshot.fieldIds);
    template = template.filter(field => allowed.has(field.id));
    values = Object.fromEntries(template.flatMap(field => [
      [field.id, values![field.id]], [observationKey(field.id), values![observationKey(field.id)]],
    ]));
  } else {
    const historicalRead = await checklistHistoricalReadService.resolve({
      checklist,
      currentFields: template,
      currentValues: values,
      lifecycle: 'existing',
    });
    template = historicalRead.fields;
    values = historicalRead.values;
  }

  // 1) Dados da empresa
  const empresa = publicSnapshot ? publicSnapshot.company : await databaseService.readItem<any>(
    APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME,
    checklist.empresa_id,
  );

  // 2) Criação do documento
  const pdfDoc = await PDFDocument.create();
  let page = pdfDoc.addPage();
  const { width, height } = page.getSize();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const safeText = (text: unknown) => sanitizeTextForPdf(String(text ?? ''), helvetica);
  const prepareMediaImage = async (
    field: ChecklistField,
    source: string,
    observation: string,
  ): Promise<PdfMediaImagePreparation> => {
    let stage: 'read' | 'embed' = 'read';
    try {
      const buffer = await fetchArrayBuffer(source, {
        checklistId: String(checklist.id ?? ''),
        companyId: String(checklist.empresa_id ?? ''),
        fieldId: String(field.id ?? ''),
        originalFieldId: typeof field.original_campo_id === 'string' ? field.original_campo_id : null,
      }, Boolean(publicSnapshot));
      stage = 'embed';
      const image = await embedPdfImage(pdfDoc, buffer, source);
      let scale = Math.min(
        LAYOUT.IMAGE_MAX_WIDTH / image.width,
        LAYOUT.IMAGE_MAX_HEIGHT / image.height,
        1,
      );
      let imageWidth = image.width * scale;
      let imageHeight = image.height * scale;
      const observationReserve = observation ? LAYOUT.LINE_HEIGHT * 1.5 : 0;
      const labelLines = wrapTextToWidth(safeText(`${LAYOUT.BULLET}${field.nome}:`), helveticaBold, LAYOUT.LABEL_FONT_SIZE, width - 2 * LAYOUT.PAGE_MARGIN);
      const labelHeight = labelLines.length * LAYOUT.LINE_HEIGHT;
      {
        const pageContentHeight = page.getSize().height - 2 * LAYOUT.PAGE_MARGIN;
        const nonImageHeight = LAYOUT.SECTION_TITLE_FONT_SIZE + 2 * LAYOUT.GROUP_HEADER_PADDING_Y + LAYOUT.GROUP_AFTER_SPACING + labelHeight + LAYOUT.VALUE_FONT_SIZE + LAYOUT.LINE_HEIGHT + observationReserve;
        const maxImageHeight = Math.max(1, pageContentHeight - nonImageHeight);
        if (imageHeight > maxImageHeight) {
          scale = maxImageHeight / image.height;
          imageWidth = image.width * scale;
          imageHeight = image.height * scale;
        }
      }
      return {
        status: 'prepared',
        value: {
          image,
          imageWidth,
          imageHeight,
          scale,
          blockHeight: labelHeight + imageHeight + LAYOUT.VALUE_FONT_SIZE + LAYOUT.LINE_HEIGHT + observationReserve,
          labelLines,
        },
      };
    } catch (error) {
      return { status: 'failed', error, stage };
    }
  };

  // Cursor vertical
  let y = height - LAYOUT.PAGE_MARGIN;
  const ensureSpace = (needed: number) => {
    if (y - needed < LAYOUT.PAGE_MARGIN) {
      page = pdfDoc.addPage();
      y = page.getSize().height - LAYOUT.PAGE_MARGIN;
    }
  };
  const contentWidth = width - 2 * LAYOUT.PAGE_MARGIN;
  const drawWrappedText = (
    text: string,
    font: PDFFont,
    fontSize: number,
    x: number = LAYOUT.PAGE_MARGIN,
    maxWidth: number = contentWidth,
  ) => {
    const lines = wrapTextToWidth(text, font, fontSize, maxWidth);
    lines.forEach((line) => {
      ensureSpace(LAYOUT.LINE_HEIGHT);
      if (line) page.drawText(line, { x, y: y - fontSize, size: fontSize, font });
      y -= LAYOUT.LINE_HEIGHT;
    });
  };
  const renderImageFieldFailure = (
    field: ChecklistField,
    source: string,
    error: unknown,
    kindOverride?: PdfImageLoadFailureKind,
  ) => {
    const failureKind = kindOverride ?? classifyPdfImageLoadFailure(error, source);
    const failureMessage = getPdfImageLoadFailureMessage(failureKind);
    if (publicSnapshot) {
      PdfDocLogger.Main.warn('[PDF] Mídia indisponível no comprovante', { failureKind });
      throw Object.assign(new Error('Uma imagem compartilhada está indisponível ou foi removida. Tente novamente ou solicite o PDF à empresa.'), {
        code: 'partner_receipt_media_unavailable',
      });
    }
    const sourceInfo = describeImageSourceForLog(source);

    PdfDocLogger.Main.warn('[PDF] Falha ao embedar imagem; campo mantido com aviso', {
      checklistId: checklist.id,
      campoId: field.id,
      campoNome: field.nome,
      campoTipo: field.tipo,
      failureKind,
      ...sourceInfo,
      error,
    });

    captureSentryExceptionWithScope(error, {
      level: failureKind === 'network' ? 'warning' : 'error',
      context: {
        flow: 'checklist_pdf',
        stage: 'embed_image',
        checklistId: checklist.id,
        campoId: field.id,
        campoNome: field.nome,
        campoTipo: field.tipo,
        failureKind,
        ...sourceInfo,
      },
      tags: {
        flow: 'checklist_pdf',
        stage: 'embed_image',
        failure_kind: failureKind,
      },
      fingerprint: ['checklist_pdf', 'embed_image', failureKind],
      dedupeKey: `checklist_pdf:embed_image:${failureKind}:${checklist.id}:${field.id}`,
    });

    return failureMessage;
  };

  // ------------------ Cabeçalho ------------------ //
  const originalHeader = checklist.empresa_id === ORIGINAL_HEADER_COMPANY_ID;
  const companyWidth = originalHeader ? contentWidth : contentWidth * 0.58;
  const brandX = LAYOUT.PAGE_MARGIN + companyWidth + 18;
  const brandWidth = width - LAYOUT.PAGE_MARGIN - brandX;
  const headerStartY = y;
  let brandBottomY = headerStartY;
  if (!originalHeader) {
    const addBrandLink = (x: number, baseline: number, linkWidth: number, size: number, uri: string) => {
      page.node.addAnnot(pdfDoc.context.register(pdfDoc.context.obj({
        Type: 'Annot', Subtype: 'Link', Rect: [x, baseline - 2, x + linkWidth, baseline + size + 1],
        Border: [0, 0, 0], A: { Type: 'Action', S: 'URI', URI: PDFString.of(uri) },
      })));
    };
    try {
      let asset = Asset.fromModule(require('../assets/icon.png'));
      // Recursos drawable do Android precisam virar arquivo para o PDF ler os bytes.
      if (!asset.uri.includes(':') && !asset.uri.includes('/')) {
        asset = new Asset({ name: asset.name, type: asset.type, uri: asset.uri });
      }
      await asset.downloadAsync();
      const icon = await pdfDoc.embedPng(await fetchArrayBuffer(asset.localUri ?? asset.uri, undefined, Boolean(publicSnapshot)));
      const iconSize = 32;
      const iconX = brandX + (brandWidth - iconSize) / 2;
      const iconY = headerStartY - iconSize;
      const radius = 6;
      const curve = radius * 0.55228475;
      page.pushOperators(pushGraphicsState(),
        moveTo(iconX + radius, iconY), lineTo(iconX + iconSize - radius, iconY),
        appendBezierCurve(iconX + iconSize - radius + curve, iconY, iconX + iconSize, iconY + radius - curve, iconX + iconSize, iconY + radius),
        lineTo(iconX + iconSize, iconY + iconSize - radius),
        appendBezierCurve(iconX + iconSize, iconY + iconSize - radius + curve, iconX + iconSize - radius + curve, iconY + iconSize, iconX + iconSize - radius, iconY + iconSize),
        lineTo(iconX + radius, iconY + iconSize),
        appendBezierCurve(iconX + radius - curve, iconY + iconSize, iconX, iconY + iconSize - radius + curve, iconX, iconY + iconSize - radius),
        lineTo(iconX, iconY + radius),
        appendBezierCurve(iconX, iconY + radius - curve, iconX + radius - curve, iconY, iconX + radius, iconY),
        closePath(), clip(), endPath());
      try {
        page.drawImage(icon, { x: iconX, y: iconY, width: iconSize, height: iconSize });
      } finally {
        page.pushOperators(popGraphicsState());
      }
      brandBottomY -= iconSize + 6;
    } catch (error) {
      PdfDocLogger.Main.info('[PDF] Falha ao carregar ícone do App Guincho', { error });
    }
    const drawBrandText = (text: string, link?: string, singleLine = false) => {
      const textSize = singleLine ? Math.min(9, 9 * brandWidth / helvetica.widthOfTextAtSize(safeText(text), 9)) : 9;
      (singleLine ? [safeText(text)] : wrapTextToWidth(safeText(text), helvetica, textSize, brandWidth)).forEach(line => {
        const lineWidth = helvetica.widthOfTextAtSize(line, textSize);
        const x = brandX + (brandWidth - lineWidth) / 2;
        brandBottomY -= 12;
        page.drawText(line, { x, y: brandBottomY, size: textSize, font: helvetica, color: link ? LINK_COLOR : VALUE_COLOR });
        if (link) {
          page.drawLine({ start: { x, y: brandBottomY - 1 }, end: { x: x + lineWidth, y: brandBottomY - 1 }, thickness: 0.4, color: LINK_COLOR });
          addBrandLink(x, brandBottomY, lineWidth, textSize, link);
        }
      });
    };
    drawBrandText('App Guincho - Checklists Digitais para Guincho');
    brandBottomY -= 4;
    drawBrandText('Conheça mais em:');
    drawBrandText(APP_SITE.replace('https://', ''), APP_SITE, true);
    const contactText = 'Ou fale conosco +55 (31) 98336-4526';
    const contactSize = 8.2;
    const contactWidth = helvetica.widthOfTextAtSize(contactText, contactSize) + 14;
    const contactX = brandX + (brandWidth - contactWidth) / 2;
    brandBottomY -= 14;
    page.drawSvgPath(WHATSAPP_ICON_PATH, { x: contactX, y: brandBottomY + 9, scale: 11 / 24, color: rgb(0x25 / 255, 0xd3 / 255, 0x66 / 255) });
    page.drawText(contactText, { x: contactX + 14, y: brandBottomY, size: contactSize, font: helvetica, color: VALUE_COLOR });
    addBrandLink(contactX, brandBottomY, contactWidth, 11, APP_WHATSAPP);
  }
  let logoWidth = 0;
  let logoHeight = 0;
  if (empresa?.logo_url) {
    try {
      const imgBuf = await fetchArrayBuffer(empresa.logo_url, publicSnapshot ? {
        checklistId: checklist.id, companyId: empresa.id, fieldId: 'company_logo',
      } : undefined, Boolean(publicSnapshot));
      const img = await embedPdfImage(pdfDoc, imgBuf, empresa.logo_url);
      const scale = Math.min(LAYOUT.LOGO_MAX_WIDTH / img.width, LAYOUT.LOGO_MAX_HEIGHT / img.height, 1);
      logoWidth = img.width * scale;
      logoHeight = img.height * scale;
      page.drawImage(img, {
        x: LAYOUT.PAGE_MARGIN,
        y: headerStartY - logoHeight,
        width: logoWidth,
        height: logoHeight,
      });
    } catch (e) {
      PdfDocLogger.Main.info('[PDF] Falha ao carregar logo da empresa', { error: publicSnapshot ? 'Logo indisponível' : e });
    }
  }

  const textX = LAYOUT.PAGE_MARGIN + (logoWidth ? logoWidth + 12 : 0);
  let textY = headerStartY - LAYOUT.HEADER_FONT_SIZE;
  const drawHeaderLine = (txt: string) => {
    const lines = originalHeader ? [safeText(txt)] : wrapTextToWidth(safeText(txt), helvetica, LAYOUT.VALUE_FONT_SIZE, LAYOUT.PAGE_MARGIN + companyWidth - textX);
    lines.forEach(line => {
      page.drawText(line, { x: textX, y: textY, size: LAYOUT.VALUE_FONT_SIZE, font: helvetica, color: rgb(0.3, 0.3, 0.3) });
      textY -= LAYOUT.VALUE_FONT_SIZE + LAYOUT.HEADER_LINE_SPACING;
    });
  };

  if (empresa?.nome) {
    const lines = originalHeader ? [safeText(empresa.nome)] : wrapTextToWidth(safeText(empresa.nome), helveticaBold, LAYOUT.HEADER_FONT_SIZE, LAYOUT.PAGE_MARGIN + companyWidth - textX);
    lines.forEach(line => {
      page.drawText(line, { x: textX, y: textY, size: LAYOUT.HEADER_FONT_SIZE, font: helveticaBold });
      textY -= originalHeader ? LAYOUT.VALUE_FONT_SIZE + LAYOUT.HEADER_LINE_SPACING : LAYOUT.HEADER_FONT_SIZE + LAYOUT.HEADER_LINE_SPACING;
    });
  }
  if (empresa?.cnpj) drawHeaderLine(`CNPJ: ${empresa.cnpj}`);
  if (empresa?.telefone) drawHeaderLine(`Tel: ${empresa.telefone}`);
  if (empresa?.email) drawHeaderLine(`Email: ${empresa.email}`);

  const enderecoLines: string[] = [];
  if (empresa?.endereco) enderecoLines.push(empresa.endereco);
  const cidadeUf = [empresa?.cidade, empresa?.uf].filter(Boolean).join(' - ');
  const cepStr = empresa?.cep ? `CEP: ${empresa.cep}` : '';
  const cidadeCepLine = [cidadeUf, cepStr].filter(Boolean).join(' | ');
  if (cidadeCepLine) enderecoLines.push(cidadeCepLine);
  enderecoLines.forEach(drawHeaderLine);

  const headerBottomY = Math.min(headerStartY - logoHeight, textY, brandBottomY);
  if (!originalHeader) page.drawLine({
    start: { x: brandX - 9, y: headerStartY - 3 }, end: { x: brandX - 9, y: headerBottomY + 4 },
    thickness: 0.5, color: rgb(0.86, 0.89, 0.93),
  });
  page.drawRectangle({
    x: LAYOUT.PAGE_MARGIN,
    y: headerBottomY - 4,
    width: width - 2 * LAYOUT.PAGE_MARGIN,
    height: 1,
    color: rgb(0.8, 0.8, 0.8),
  });
  y = headerBottomY - LAYOUT.LINE_HEIGHT;
  if (publicSnapshot) drawWrappedText('Comprovante dos campos compartilhados', helvetica, 10);

  // Identificação e resumo em três colunas, com altura calculada pelo texto.
  const sequentialText = checklist.sequencial_codigo?.trim() ||
    (typeof checklist.sequencial_numero === 'number' && Number.isFinite(checklist.sequencial_numero)
      ? String(checklist.sequencial_numero) : 'Aguardando numeração');
  drawWrappedText(safeText(`Número do checklist: ${sequentialText} - Identificador: ${checklist.id}`), helvetica, 12);
  y -= 8;
  const statusInfo = STATUS_CHIP[checklist.status === 'done' ? 'done' : 'in_progress'];
  const summaryWidth = (contentWidth - 16) / 3;
  const summary = [
    { label: 'Criado em', value: formatDate(checklist.data_criacao), color: VALUE_COLOR },
    { label: 'Concluído em', value: checklist.data_conclusao ? formatDate(checklist.data_conclusao) : 'Não concluído', color: VALUE_COLOR },
    { label: 'Status', value: statusInfo.label, color: rgb(statusInfo.color.r, statusInfo.color.g, statusInfo.color.b) },
  ];
  const summarySize = Math.min(9, ...summary.map(item => 9 * summaryWidth / helvetica.widthOfTextAtSize(`${item.label}: ${item.value}`, 9)));
  const summaryHeight = summarySize + 4;
  ensureSpace(summaryHeight);
  summary.forEach((item, column) => {
    const x = LAYOUT.PAGE_MARGIN + column * (summaryWidth + 8);
    const label = `${item.label}: `;
    page.drawText(label, { x, y: y - summarySize, size: summarySize, font: helvetica, color: LABEL_COLOR });
    page.drawText(safeText(item.value), { x: x + helvetica.widthOfTextAtSize(label, summarySize),
      y: y - summarySize, size: summarySize, font: helvetica, color: item.color });
  });
  y -= summaryHeight + 12;

  // ------------------ Conteúdo ------------------ //
  const groupMap: Record<string, ChecklistField[]> = {};
  template.forEach((field) => {
    if ((values[field.id] != null && values[field.id] !== '') || values[observationKey(field.id)]?.trim()) {
      const grp = field.grupo || 'Geral';
      if (!groupMap[grp]) groupMap[grp] = [];
      groupMap[grp].push(field);
    }
  });
  Object.values(groupMap).forEach((arr) => arr.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)));

  // Pré-carrega metadata de assinaturas para evitar múltiplas queries
  let photoMetaByCampo: Record<string, any> = publicSnapshot?.mediaMetadata ?? {};
  let signMetaByCampo: Record<string, any> = publicSnapshot?.mediaMetadata ?? {};
  if (!publicSnapshot && template.some(field => ['assinatura', 'signature', 'foto', 'photo'].includes(field.tipo))) {
    try {
      const rows = await databaseService.listItems<any>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME,
        { checklist_id: checklist.id }
      );
      rows.forEach(row => {
        const fieldId = row.snapshot_field_id ?? row.campo_id;
        if (fieldId && !photoMetaByCampo[fieldId]) photoMetaByCampo[fieldId] = row;
        if (!signMetaByCampo[row.campo_id]) signMetaByCampo[row.campo_id] = row;
      });
    } catch (e) {
      PdfDocLogger.Main.info('[PDF] Falha ao pré-carregar metadata de fotos e assinaturas', { error: e });
    }
  }

  const gap = 8;
  const padding = 7;
  const fontSize = 8.2;
  const leading = 11;
  const navy = rgb(0.08, 0.16, 0.28);
  const isSignature = (field: ChecklistField) => ['assinatura', 'signature'].includes(field.tipo);
  const isPhoto = (field: ChecklistField) => ['foto', 'photo'].includes(field.tipo);
  const photoMetadata = (field: ChecklistField) => photoMetaByCampo[field.id] ?? photoMetaByCampo[field.original_campo_id];
  const isAccessory = (field: ChecklistField) => /acess[oó]rios/i.test(field.grupo ?? '') && field.tipo.startsWith('dropdown');
  const columnsFor = (field: ChecklistField) => isPhoto(field) ? 2 : isAccessory(field) ? 4 : 3;
  type CardLine = { text: string; bold: boolean; observation?: boolean; checked?: boolean; timestamp?: boolean };
  type Card = { field: ChecklistField; lines: CardLine[]; image?: PreparedPdfMediaImage; imageDrawn?: boolean; labelRemaining?: number; centered?: boolean };
  const cellWidthFor = (columns: number) => (contentWidth - gap * (columns - 1)) / columns;
  const cardLines = (text: string, bold: boolean, cellWidth: number, observation = false): CardLine[] => (
    wrapTextToWidth(safeText(text), bold ? helveticaBold : helvetica, fontSize, cellWidth - 2 * padding)
      .map(line => ({ text: line, bold, observation }))
  );
  const groupTitleLines = (group: string, continuation = false) => wrapTextToWidth(
    safeText(`${group}${continuation ? ' (continuação)' : ''}`), helveticaBold, 10, contentWidth - 18,
  );
  const groupTitleHeight = (group: string, continuation = false) => groupTitleLines(group, continuation).length * 12 + 12;
  const drawGroupTitle = (group: string, continuation = false) => {
    const lines = groupTitleLines(group, continuation);
    const headerHeight = groupTitleHeight(group, continuation);
    page.drawRectangle({ x: LAYOUT.PAGE_MARGIN, y: y - headerHeight, width: contentWidth, height: headerHeight, color: navy });
    lines.forEach((line, index) => page.drawText(line, {
      x: LAYOUT.PAGE_MARGIN + 9, y: y - 16 - index * 12, size: 10, font: helveticaBold, color: rgb(1, 1, 1),
    }));
    y -= headerHeight + 6;
  };
  const nextGroupPage = (group: string) => {
    page = pdfDoc.addPage();
    y = page.getHeight() - LAYOUT.PAGE_MARGIN;
    drawGroupTitle(group, true);
  };
  const continuationLines = (card: Card, cellWidth: number) => card.field.tipo === 'mensagem pronta' ? [] : cardLines(
    `${card.field.nome.slice(0, 80)} — ${card.lines[0]?.observation ? 'observação' : 'campo'} (continuação)`, true, cellWidth,
  );
  const cardHeight = (card: Card) => 2 * padding + card.lines.length * leading + (card.image && !card.imageDrawn ? card.image.imageHeight + gap : 0);

  // Cada continuação repete a identidade do campo e mantém a nota na sua coluna.
  const drawCards = (cards: Card[], columns: number, group: string) => {
    const cellWidth = cellWidthFor(columns);
    let continuation = false;
    while (cards.some(card => card.lines.length || (card.image && !card.imageDrawn))) {
      const titleHeight = (card: Card) => continuation && (card.lines.length || (card.image && !card.imageDrawn)) ? continuationLines(card, cellWidth).length * leading : 0;
      const remainingHeight = Math.max(...cards.map(card => cardHeight(card) + titleHeight(card)));
      const available = y - LAYOUT.PAGE_MARGIN;
      const firstMinimum = Math.max(...cards.map(card => 2 * padding + titleHeight(card) +
        (card.image && !card.imageDrawn ? (card.labelRemaining ?? 0) > 4 ? 4 * leading : (card.labelRemaining ?? 0) * leading + card.image.imageHeight + gap + (card.lines.some(line => line.timestamp) ? leading : 0) + (card.lines.some(line => line.observation) ? leading : 0) : Math.min(card.lines.length, 4) * leading)));
      if (available < Math.min(remainingHeight, firstMinimum)) {
        nextGroupPage(group);
        continue;
      }
      const rowHeight = Math.min(remainingHeight, y - LAYOUT.PAGE_MARGIN);
      cards.forEach((card, column) => {
        if (!card.lines.length && (!card.image || card.imageDrawn)) return;
        const x = LAYOUT.PAGE_MARGIN + column * (cellWidth + gap);
        page.drawRectangle({ x, y: y - rowHeight, width: cellWidth, height: rowHeight,
          borderWidth: 0.6, borderColor: rgb(0.84, 0.87, 0.91), color: rgb(1, 1, 1) });
        let cellY = y - padding;
        if (continuation) {
          const titleLines = continuationLines(card, cellWidth);
          titleLines.forEach(line => {
            page.drawText(line.text, { x: x + padding, y: cellY - fontSize, size: fontSize, font: helveticaBold, color: LABEL_COLOR });
            cellY -= leading;
          });
        }
        const drawLine = () => {
          const line = card.lines.shift()!;
          const font = line.bold ? helveticaBold : helvetica;
          const lineSize = line.timestamp ? 7.4 : fontSize;
          if (line.checked) {
            const boxX = x + padding;
            const boxY = cellY - fontSize;
            page.drawRectangle({ x: boxX, y: boxY, width: 8.2, height: 8.2, borderWidth: 0.6, borderColor: VALUE_COLOR });
            page.drawLine({ start: { x: boxX + 1.5, y: boxY + 4 }, end: { x: boxX + 3.4, y: boxY + 1.8 }, thickness: 0.8, color: VALUE_COLOR });
            page.drawLine({ start: { x: boxX + 3.4, y: boxY + 1.8 }, end: { x: boxX + 6.9, y: boxY + 6.5 }, thickness: 0.8, color: VALUE_COLOR });
          }
          if (line.text) page.drawText(line.text, {
            x: card.centered || line.timestamp ? x + (cellWidth - font.widthOfTextAtSize(line.text, lineSize)) / 2 : x + padding + (line.checked ? 13 : 0),
            y: cellY - lineSize, size: lineSize, font, color: line.bold || line.timestamp ? LABEL_COLOR : VALUE_COLOR,
          });
          cellY -= leading;
        };
        if (card.image && !card.imageDrawn) {
          while (card.labelRemaining && cellY - leading >= y - rowHeight + padding) {
            drawLine();
            card.labelRemaining--;
          }
          if (card.labelRemaining || cellY - card.image.imageHeight - gap < y - rowHeight + padding) return;
          try {
            page.drawImage(card.image.image, { x: x + (cellWidth - card.image.imageWidth) / 2,
              y: cellY - card.image.imageHeight, width: card.image.imageWidth, height: card.image.imageHeight });
            cellY -= card.image.imageHeight + gap;
          } catch (error) {
            const message = renderImageFieldFailure(card.field, String(values![card.field.id]), error, 'invalid')!;
            card.lines.unshift(...cardLines(message, false, cellWidth));
          }
          card.imageDrawn = true;
        }
        while (card.lines.length && cellY - leading >= y - rowHeight + padding - 0.001) drawLine();
      });
      y -= rowHeight + gap;
      if (cards.some(card => card.lines.length || (card.image && !card.imageDrawn))) {
        nextGroupPage(group);
        continuation = true;
      }
    }
  };

  for (const [grupo, originalFields] of Object.entries(groupMap)) {
    const headerHeight = groupTitleHeight(grupo);
    // Mantém as combinações de endereço já usadas pelo PDF de produção.
    const pairedIds = new Set<string>();
    const combinedValues = new Map<string, string>();
    originalFields.forEach(field => {
      const pairedType = field.tipo === 'cep2' ? 'cep3' : field.tipo === 'cep5' ? 'cep6' : null;
      const paired = pairedType ? originalFields.find(candidate => candidate.tipo === pairedType) : null;
      if (paired && values![paired.id]) {
        combinedValues.set(field.id, `${values![field.id]}${field.tipo === 'cep2' ? ', ' : ' - UF: '}${values![paired.id]}`);
        pairedIds.add(paired.id);
      }
    });
    const fields = originalFields.filter(field => !pairedIds.has(field.id));
    let groupStarted = false;
    for (let start = 0; start < fields.length;) {
      const field = fields[start];
      const media = MEDIA_OBSERVATION_FIELD_TYPES.has(field.tipo);
      const signature = isSignature(field);
      const columns = signature ? 1 : columnsFor(field);
      const chunk = [field];
      if (!signature) {
        while (chunk.length < columns && start + chunk.length < fields.length) {
          const candidate = fields[start + chunk.length];
          if (isSignature(candidate) || MEDIA_OBSERVATION_FIELD_TYPES.has(candidate.tipo) !== media || columnsFor(candidate) !== columns) break;
          chunk.push(candidate);
        }
      }
      if (signature) {
        const source = String(values[field.id] ?? '');
        const prepared = source ? await prepareMediaImage(field, source, '') : null;
        const lines = cardLines(field.nome, true, contentWidth);
        const card: Card = { field, lines, centered: true };
        if (prepared?.status === 'prepared') {
          card.labelRemaining = lines.length;
          card.image = prepared.value;
        } else {
          const message = prepared?.status === 'failed'
            ? renderImageFieldFailure(field, source, prepared.error, prepared.stage === 'embed' ? 'invalid' : undefined)!
            : 'Não preenchido';
          lines.push(...cardLines(message, false, contentWidth));
        }
        const row = signMetaByCampo[field.id];
        if (row?.created_at) lines.push(...cardLines(`Assinatura feita em: ${formatDateFull(row.created_at)}`, false, contentWidth));
        lines.push(...cardLines(`Identificador da assinatura: ${field.id}`, false, contentWidth));
        const completeHeight = cardHeight(card);
        const reserve = completeHeight <= page.getHeight() - 2 * LAYOUT.PAGE_MARGIN - headerHeight - 6
          ? completeHeight : 2 * padding + 4 * leading;
        if (!groupStarted) {
          ensureSpace(headerHeight + 6 + reserve);
          drawGroupTitle(grupo);
          groupStarted = true;
        }
        if (y - reserve < LAYOUT.PAGE_MARGIN) nextGroupPage(grupo);
        drawCards([card], 1, grupo);
      } else {
        const cellWidth = cellWidthFor(columns);
        const cards: Card[] = [];
        for (const item of chunk) {
          const observation = values[observationKey(item.id)]?.trim() ?? '';
          const readyMessage = item.tipo === 'mensagem pronta';
          const lines = readyMessage ? [] : cardLines(`${item.nome}:`, true, cellWidth);
          const card: Card = { field: item, lines };
          if (media && values[item.id]) {
            const result = await prepareMediaImage(item, String(values[item.id]), observation);
            if (result.status === 'prepared') {
              const prepared = result.value;
              const scale = Math.min((cellWidth - 2 * padding) / prepared.image.width, (isPhoto(item) ? 210 : 125) / prepared.image.height, 1);
              card.labelRemaining = lines.length;
              card.image = { ...prepared, imageWidth: prepared.image.width * scale,
                imageHeight: prepared.image.height * scale, labelLines: lines.map(line => line.text) };
              const updatedAt = isPhoto(item) ? formatPhotoUpdatedAt(photoMetadata(item)?.updated_at) : '';
              if (updatedAt) lines.push({ text: updatedAt, bold: false, timestamp: true });
            } else {
              const message = renderImageFieldFailure(item, String(values[item.id]), result.error, result.stage === 'embed' ? 'invalid' : undefined)!;
              lines.push(...cardLines(message, false, cellWidth));
            }
          } else {
            const value = item.tipo === 'timestamp with time zone'
              ? formatPhotoUpdatedAt(values[item.id]) || 'Não preenchido'
              : combinedValues.get(item.id) ?? String(values[item.id] || 'Não preenchido');
            if (readyMessage) {
              const messageLines = wrapTextToWidth(safeText(value), helvetica, fontSize, cellWidth - 2 * padding - 13);
              lines.push(...messageLines.map((text, index) => ({ text, bold: false, checked: index === 0 && Boolean(values[item.id]) })));
            } else lines.push(...cardLines(value, false, cellWidth));
          }
          if (observation) lines.push(...cardLines(`Observação: ${observation}`, false, cellWidth, true));
          cards.push(card);
        }
        if (!groupStarted) {
          const firstHeight = Math.max(...cards.map(card => Math.min(cardHeight(card),
            2 * padding + (card.image ? card.image.labelLines.length * leading + card.image.imageHeight + gap + (card.lines.some(line => line.timestamp) ? leading : 0) + (card.lines.some(line => line.observation) ? leading : 0) : 4 * leading))));
          ensureSpace(headerHeight + 6 + firstHeight);
          drawGroupTitle(grupo);
          groupStarted = true;
        }
        drawCards(cards, columns, grupo);
      }
      start += chunk.length;
    }
    y -= 5;
  }

  const pages = pdfDoc.getPages();
  pages.forEach((pdfPage, index) => {
    const label = `${index + 1} / ${pages.length}`;
    pdfPage.drawText(label, { x: (pdfPage.getWidth() - helvetica.widthOfTextAtSize(label, 9)) / 2, y: 18, size: 9, font: helvetica, color: rgb(0.5, 0.5, 0.5) });
  });
  return pdfDoc;
}

// Exportações auxiliares (úteis em testes)
export default {
  buildChecklistPdfDoc,
  formatDate,
  formatDateFull,
  wrapTextToWidth,
  LAYOUT,
  IMAGE_FIELD_TYPES,
  classifyPdfImageLoadFailure,
  getPdfImageLoadFailureMessage,
  PDF_IMAGE_LOAD_FAILURE_MESSAGES,
}; 
